import React, { useEffect, useState } from 'react';
import { FormControl, CircularProgress, Autocomplete, TextField, Box, Typography, Button } from '@mui/material';
import { getGroups } from '../api/groups';
import { GroupInterface } from '../types';

interface VariantSelectProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  clearTrigger?: number;
}

interface VariantOption {
  id: string;
  name: string;
  groupName: string;
  groupId: string;
}

function VariantSelect({ value, onChange, label = 'Course', disabled, clearTrigger }: VariantSelectProps): React.JSX.Element {
  const [groups, setGroups] = useState<GroupInterface[]>([]);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLabel, setSelectedLabel] = useState('');

  useEffect(() => {
    fetchGroupsAndVariants();
  }, []);

  useEffect(() => {
    setSearchTerm('');
  }, [clearTrigger]);

  // When value changes, update selectedLabel
  useEffect(() => {
    if (!value) {
      setSelectedLabel('');
    } else {

      const allVariants = groups.flatMap(group => (group.variants || []).map(variant => ({ ...variant, groupName: group.name })));
      const selected = allVariants.find(v => +v.id === +value);

      if (selected) {
        setSelectedLabel(`${selected.name} (${selected.groupName})`);
      } else {
        setSelectedLabel('');
      }
    }
  }, [value, groups]);

  const fetchGroupsAndVariants = async () => {
    setDropdownLoading(true);
    try {
      const res = await getGroups('inactive_variants=1');
      const groupsData: GroupInterface[] = res.data || [];
      setGroups(groupsData);
    } finally {
      setDropdownLoading(false);
    }
  };

  // Create flat list of variant options with group info
  const variantOptions: VariantOption[] = groups.flatMap(group =>
    (group.variants || []).map(variant => ({
      id: variant.id,
      name: variant.name,
      groupName: group.name,
      groupId: group.id
    }))
  );

  // Filter options based on search term
  const filteredOptions = variantOptions.filter(option =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    option.groupName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Find the selected option
  const selectedOption = value ? variantOptions.find(option => option.id === value) : null;

  if (value && selectedLabel) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: 'solid 1px black', padding: 1 }}>
        <Typography fontWeight="bold">{selectedLabel}</Typography>
        <Button variant="outlined" size="small" onClick={() => onChange('')}>
          X
        </Button>
      </Box>
    );
  }

  return (
    <FormControl size="small" fullWidth>
      <Autocomplete
        value={selectedOption}
        onChange={(_, newValue) => onChange(newValue?.id || '')}
        options={filteredOptions}
        getOptionLabel={(option) => `${option.name} (${option.groupName})`}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        loading={dropdownLoading}
        disabled={disabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {dropdownLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props;
          return (
            <li key={key} {...rest}>
              <div>
                <div style={{ fontWeight: 'bold' }}>{option.name}</div>
                <div style={{ fontSize: '0.8em', color: 'gray' }}>{option.groupName}</div>
              </div>
            </li>
          );
        }}
        noOptionsText="לא נמצאו קורסים"
        clearOnBlur={false}
        clearOnEscape={false}
      />
    </FormControl>
  );
}

export default VariantSelect; 